import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { webhookApi } from '@/services/api'

// Types for webhook data
export interface Webhook {
  id: string
  name: string
  event_types: string[]
  is_active: boolean
  last_triggered_at?: string
  last_failure_message?: string
  created_at: string
  updated_at: string
}

export interface CreateWebhookData {
  name: string
  url: string
  event_types: string[]
  is_active?: boolean
}

export interface UpdateWebhookData {
  name?: string
  url?: string
  event_types?: string[]
  is_active?: boolean
}

// Hook to fetch webhooks for a program
export function useWebhooks(programId: string | undefined) {
  return useQuery<{ webhooks: Webhook[] }, Error>({
    queryKey: ['webhooks', programId],
    queryFn: () => {
      if (!programId) throw new Error('Program ID is required')
      return webhookApi.getWebhooks(programId)
    },
    enabled: !!programId,
  })
}

// Hook to create a webhook
export function useCreateWebhook() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ programId, data }: { programId: string; data: CreateWebhookData }) =>
      webhookApi.createWebhook(programId, data),
    onSuccess: (_, { programId }) => {
      queryClient.invalidateQueries({ queryKey: ['webhooks', programId] })
      toast({
        title: 'Success',
        description: 'Webhook created successfully',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create webhook',
        variant: 'destructive',
      })
    },
  })
}

// Hook to update a webhook
export function useUpdateWebhook() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ 
      programId, 
      webhookId, 
      data 
    }: { 
      programId: string
      webhookId: string
      data: UpdateWebhookData 
    }) => webhookApi.updateWebhook(programId, webhookId, data),
    onSuccess: (_, { programId }) => {
      queryClient.invalidateQueries({ queryKey: ['webhooks', programId] })
      toast({
        title: 'Success',
        description: 'Webhook updated successfully',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update webhook',
        variant: 'destructive',
      })
    },
  })
}

// Hook to delete a webhook
export function useDeleteWebhook() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ programId, webhookId }: { programId: string; webhookId: string }) =>
      webhookApi.deleteWebhook(programId, webhookId),
    onSuccess: (_, { programId }) => {
      queryClient.invalidateQueries({ queryKey: ['webhooks', programId] })
      toast({
        title: 'Success',
        description: 'Webhook deleted successfully',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete webhook',
        variant: 'destructive',
      })
    },
  })
}

// Hook to test a webhook
export function useTestWebhook() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ programId, webhookId }: { programId: string; webhookId: string }) =>
      webhookApi.testWebhook(programId, webhookId),
    onSuccess: (data, { programId }) => {
      queryClient.invalidateQueries({ queryKey: ['webhooks', programId] })
      
      if (data.success) {
        toast({
          title: 'Test Successful',
          description: 'Webhook test completed successfully',
        })
      } else {
        toast({
          title: 'Test Failed',
          description: data.message || 'Webhook test failed',
          variant: 'destructive',
        })
      }
    },
    onError: (error: Error, { programId }) => {
      queryClient.invalidateQueries({ queryKey: ['webhooks', programId] })
      
      toast({
        title: 'Test Failed',
        description: error.message || 'Failed to test webhook',
        variant: 'destructive',
      })
    },
  })
}